import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { getPlatformLabel } from './bucketLabels';

/**
 * AppInfo displays app name, version, inspection name, and notes if available.
 * Props:
 *   - metadataUrl: URL to metadata.json
 *   - inspectionName: string (folder/inspection name)
 *   - appName: string (optional, preferred)
 *   - version: string (optional, preferred)
 *   - notes: string (optional, URL to notes.txt)
 */
export default function AppInfo({ metadataUrl, inspectionName, appName: propAppName, version: propVersion, notes }) {
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [notesContent, setNotesContent] = useState(null);

    useEffect(() => {
        if (!metadataUrl) return;
        fetch(metadataUrl)
            .then(res => res.json())
            .then(setMeta)
            .catch(e => setError(e.message));
    }, [metadataUrl]);

    useEffect(() => {
        if (!notes) {
            setNotesContent(null);
            return;
        }
        fetch(notes)
            .then(res => {
                if (!res.ok) throw new Error('Notes not found');
                return res.text();
            })
            .then(setNotesContent)
            .catch(e => setError('No notes available.'));
    }, [notes]);

    // Use props if provided, otherwise fallback to metadata
    let appName = propAppName || '';
    let appVersion = propVersion || '';
    if ((!appName || !appVersion) && meta && meta.Executable) {
        // Leaf of either path flavour (a POSIX path has no backslash, so the old
        // `split('\\').pop() || …` returned the whole path); drop the installer suffixes.
        const exe = meta.Executable.replace(/\\/g, '/').split('/').filter(Boolean).pop() || '';
        if (!appName) appName = exe ? exe.replace(/Setup.*$/i, '').replace(/\.(exe|app|pkg|dmg)$/i, '') : '';
        if (!appVersion && meta.Version) appVersion = meta.Version;
    }
    const platformLabel = getPlatformLabel(meta?.Platform);

    // Extract timestamp from inspectionName if present
    let inspectionTimestampLocal = '';
    if (inspectionName) {
        // Match watch_YYMMDDHHMMSS_xxx
        const m = inspectionName.match(/^watch_(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
        if (m) {
            // Format as 20YY-MM-DDTHH:MM:SSZ (ISO 8601 UTC)
            const iso = `20${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
            // Convert to local time string
            const d = new Date(iso);
            if (!isNaN(d)) {
                inspectionTimestampLocal = d.toLocaleString();
            }
        }
    }

    return (
        <Box
            width="100%"
            px={3}
            py={2}
            mb={2}
            sx={{
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
            }}
        >
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                {platformLabel && (
                    <Chip
                        size="small"
                        label={platformLabel}
                        title={meta?.PlatformVersion || platformLabel}
                        color={meta?.Platform === 'macos' ? 'secondary' : 'default'}
                        variant="outlined"
                    />
                )}
                {appVersion && (
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: 'text.primary' }}>
                        v{appVersion}
                    </Typography>
                )}
                {inspectionName && (
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, color: 'text.primary' }}>
                        {inspectionName}
                    </Typography>
                )}
                {inspectionTimestampLocal && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Local time: {inspectionTimestampLocal}
                    </Typography>
                )}
            </Box>
            {error && <Typography color="error">{error}</Typography>}
            {notes && notesContent && notesContent.trim() && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', mt: 0.5, color: 'text.primary' }}>
                    <b>Notes:</b> {notesContent.trim()}
                </Typography>
            )}
        </Box>
    );
}
